import {
  BTN_TXT,
  GLOBAL_CONSTANTS,
  HYPERVERGE_WORKFLOW_IDS,
  KYC_MODE,
  KYC_TYPE,
  MANUAL_KYC_ID_TYPE,
  MANUAL_KYC_LABELS,
  MANUAL_KYC_MSG,
  MANUAL_KYC_STEP_STATUS,
  TITLE_TXT,
  USERTYPE_ID,
} from "../constants/constants";

const { SUBMITTED, VERIFIED, REJECTED, NOT_APPLICABLE } = MANUAL_KYC_STEP_STATUS;

export const isDoneStatus = (status) =>
  status === SUBMITTED || status === VERIFIED;

export const isRejectedStatus = (status) => status === REJECTED;

export const getManualStepStatuses = (user = {}) => {
  const safeUser = user || {};
  const profile = safeUser.businessProfile || {};
  return {
    s1Status: safeUser.step1Status || profile.step1Status,
    s2Status: safeUser.step2Status || profile.step2Status,
    s3Status: safeUser.step3Status || profile.step3Status,
    s1Remarks: safeUser.step1Remarks || profile.step1Remarks,
    s2Remarks: safeUser.step2Remarks || profile.step2Remarks,
    s3Remarks: safeUser.step3Remarks || profile.step3Remarks,
    generalRemarks: safeUser.remarks || profile.remarks,
    kycType: safeUser.kycType || profile.kycType,
    orgStatusId:
      safeUser.orgStatusId || profile.orgStatusId || safeUser.userStatusId,
  };
};

export const isEntityOrNbfcUser = (userTypeId) =>
  userTypeId === USERTYPE_ID.ENTITY || userTypeId === USERTYPE_ID.NBFC;

export const isNonIndividualUser = (userTypeId) =>
  isEntityOrNbfcUser(userTypeId) || userTypeId === USERTYPE_ID.HUF;

export const getStep1Labels = ({ isHuf, isEntityOrNbfc }) => {
  if (isHuf) {
    return {
      panLabel: MANUAL_KYC_LABELS.KARTA_PAN,
      nameLabel: MANUAL_KYC_LABELS.FULL_NAME_KARTA_PAN,
      selfieLabel: MANUAL_KYC_LABELS.KARTA_SELFIE,
    };
  }
  if (isEntityOrNbfc) {
    return {
      panLabel: MANUAL_KYC_LABELS.DIRECTOR_PAN,
      nameLabel: MANUAL_KYC_LABELS.FULL_NAME_DIRECTOR_PAN,
      selfieLabel: MANUAL_KYC_LABELS.DIRECTOR_SELFIE,
    };
  }
  return {
    panLabel: MANUAL_KYC_LABELS.PAN,
    nameLabel: MANUAL_KYC_LABELS.FULL_NAME_PAN,
    selfieLabel: MANUAL_KYC_LABELS.SELFIE,
  };
};

export const getStep2Labels = ({ isHuf }) => ({
  entityPanLabel: isHuf
    ? MANUAL_KYC_LABELS.HUF_PAN
    : MANUAL_KYC_LABELS.ENTITY_PAN,
  entityDocLabel: isHuf
    ? MANUAL_KYC_LABELS.HUF_PAN_DOC
    : MANUAL_KYC_LABELS.ENTITY_PAN_DOC,
  title: isHuf
    ? TITLE_TXT.MANUAL_STEP2_HUF_FORM_TITLE
    : TITLE_TXT.MANUAL_STEP2_TITLE,
});

export const buildRejectionMetadata = ({
  isHuf,
  isCompany,
  isNbfc,
  statuses,
}) => {
  const {
    s1Status,
    s2Status,
    s3Status,
    s1Remarks,
    s2Remarks,
    s3Remarks,
    generalRemarks,
  } = statuses;
  const isEntityOrHufOrNbfc = isCompany || isHuf || isNbfc;
  const isEntityOrNbfc = isCompany || isNbfc;
  const defaultReason = generalRemarks || MANUAL_KYC_MSG.REJECT_DEFAULT;
  const rejectedSteps = [];

  if (isRejectedStatus(s1Status)) {
    rejectedSteps.push({
      stepNum: 1,
      title: isHuf
        ? TITLE_TXT.MANUAL_STEP1_KARTA_TITLE
        : isEntityOrHufOrNbfc
          ? TITLE_TXT.MANUAL_STEP1_DIRECTOR_TITLE
          : TITLE_TXT.MANUAL_STEP1_IDENTITY_TITLE,
      reason: s1Remarks || defaultReason,
      targetStep: 0,
      btnText: MANUAL_KYC_MSG.RESUBMIT_STEP1,
      isDisabled: false,
    });
  }

  if (isRejectedStatus(s2Status)) {
    rejectedSteps.push({
      stepNum: 2,
      title: isHuf
        ? TITLE_TXT.MANUAL_STEP2_HUF_TITLE
        : isEntityOrHufOrNbfc
          ? TITLE_TXT.MANUAL_STEP2_ENTITY_TITLE
          : TITLE_TXT.MANUAL_STEP2_BANK_TITLE,
      reason: s2Remarks || defaultReason,
      targetStep: 1,
      btnText: isHuf
        ? MANUAL_KYC_MSG.RESUBMIT_STEP2_HUF
        : isEntityOrNbfc
          ? MANUAL_KYC_MSG.RESUBMIT_STEP2_ENTITY
          : MANUAL_KYC_MSG.RESUBMIT_STEP2_BANK,
      isDisabled: isRejectedStatus(s1Status),
    });
  }

  if (isEntityOrHufOrNbfc && isRejectedStatus(s3Status)) {
    rejectedSteps.push({
      stepNum: 3,
      title: TITLE_TXT.MANUAL_STEP3_BANK_TITLE,
      reason: s3Remarks || defaultReason,
      targetStep: 2,
      btnText: MANUAL_KYC_MSG.RESUBMIT_STEP3_BANK,
      isDisabled: isRejectedStatus(s1Status) || isRejectedStatus(s2Status),
    });
  }

  return rejectedSteps;
};

export const getDefaultRejectionFallback = (statuses = {}) => ({
  title: TITLE_TXT.KYC_REJECTED_TITLE,
  reason: statuses.generalRemarks || MANUAL_KYC_MSG.REJECT_DEFAULT,
  targetStep: 0,
  btnText: BTN_TXT.RESUBMIT_KYC,
});

export const isManualKycSubmittedComplete = (isIndividual, statuses = {}) => {
  const { s2Status, s3Status } = statuses;
  if (isIndividual) {
    return (
      isDoneStatus(s2Status) || s3Status === NOT_APPLICABLE
    );
  }
  return isDoneStatus(s3Status);
};

export const isFullyKycVerified = (user = {}) => {
  const safeUser = user || {};
  const orgStatusId =
    safeUser.orgStatusId ||
    safeUser.businessProfile?.orgStatusId ||
    safeUser.userStatusId;
  return (
    orgStatusId === GLOBAL_CONSTANTS.STAT_KYC_VERIFIED_ID ||
    safeUser.userStatusName === "KYC_VERIFIED"
  );
};

export const hasAnyRejectedManualStep = (statuses = {}) =>
  isRejectedStatus(statuses.s1Status) ||
  isRejectedStatus(statuses.s2Status) ||
  isRejectedStatus(statuses.s3Status);

export const hasSubmittedManualProgress = ({
  kycType,
  statuses = {},
  currentStep = 0,
}) =>
  kycType === KYC_MODE.MANUAL ||
  isDoneStatus(statuses.s1Status) ||
  isDoneStatus(statuses.s2Status) ||
  isDoneStatus(statuses.s3Status) ||
  currentStep > 0;

export const resolveAutoAdvanceStep = ({
  isIndividual,
  isHUF = false,
  statuses = {},
  user = {},
  kycHistory,
  currentStep,
}) => {
  const safeUser = user || {};
  const { s1Status, s2Status } = statuses;
  const profile = safeUser.businessProfile || {};

  const s1Verified =
    isDoneStatus(s1Status) ||
    isDoneStatus(safeUser.step1Status) ||
    isDoneStatus(profile.step1Status) ||
    kycHistory?.kycPanValue?.isVerified ||
    kycHistory?.kycDirectorsPanValue?.isVerified ||
    Boolean(profile.pan || safeUser?.business?.pan) ||
    currentStep > 0;
  const s1Done = s1Verified && !isRejectedStatus(s1Status);

  const s2Verified =
    isDoneStatus(s2Status) ||
    isDoneStatus(safeUser.step2Status) ||
    isDoneStatus(profile.step2Status) ||
    kycHistory?.kycGstinValue?.isVerified ||
    (isHUF && kycHistory?.kycPanValue?.isVerified) ||
    isIndividual ||
    currentStep > 1;
  const s2Done = s2Verified && !isRejectedStatus(s2Status);

  if (!s1Done) return 0;
  if (!s2Done && !isIndividual) return 1;
  return isIndividual ? 1 : 2;
};

export const getUiStepStatus = ({
  index,
  currentStep,
  stepStatus = [],
  statuses = {},
}) => {
  const { s1Status, s2Status, s3Status } = statuses;
  if (index === 0 && isRejectedStatus(s1Status)) return "error";
  if (index === 1 && isRejectedStatus(s2Status)) return "error";
  if (index === 2 && isRejectedStatus(s3Status)) return "error";
  if (index === 0 && isDoneStatus(s1Status)) return "finish";
  if (index === 1 && isDoneStatus(s2Status)) return "finish";
  if (index === 2 && isDoneStatus(s3Status)) return "finish";
  if (index === currentStep) return "process";
  return stepStatus[index] || "wait";
};

export const getHypervergeWorkflowForStep = ({
  currentStep,
  isIndividual,
  isCompany,
  isHUF,
  isNbfc,
  orgId,
  random,
}) => {
  const kycTransactionId = `${KYC_TYPE.KYC}${orgId}${random}`;
  const gstTransactionId = `${KYC_TYPE.GST}${orgId}${random}`;
  const panTransactionId = `${KYC_TYPE.PAN}${orgId}${random}`;
  const bankTransactionId = `${KYC_TYPE.BNK}${orgId}${random}`;

  let workflowId = HYPERVERGE_WORKFLOW_IDS.KYC_WFLOW_ID;
  let transactionId = kycTransactionId;
  let type = KYC_TYPE.KYC;

  if (currentStep === 1 && isIndividual) {
    workflowId = HYPERVERGE_WORKFLOW_IDS.BANK_WFLOW_ID;
    transactionId = bankTransactionId;
    type = KYC_TYPE.BNK;
  } else if (currentStep === 1 && (isCompany || isHUF || isNbfc)) {
    if (isHUF) {
      workflowId = HYPERVERGE_WORKFLOW_IDS.PAN_WFLOW_ID;
      transactionId = panTransactionId;
      type = KYC_TYPE.PAN;
    } else {
      workflowId = HYPERVERGE_WORKFLOW_IDS.GSTIN_WFLOW_ID;
      transactionId = gstTransactionId;
      type = KYC_TYPE.GST;
    }
  } else if (currentStep === 2) {
    workflowId = HYPERVERGE_WORKFLOW_IDS.BANK_WFLOW_ID;
    transactionId = bankTransactionId;
    type = KYC_TYPE.BNK;
  }

  return { workflowId, transactionId, type };
};

export const getAsyncErrorMessage = (payloadOrError, fallback) => {
  if (typeof payloadOrError === "string" && payloadOrError) return payloadOrError;
  return (
    payloadOrError?.error ||
    payloadOrError?.errorMsg ||
    payloadOrError?.message ||
    fallback
  );
};

export const validateManualKycUploads = ({
  backendStep,
  isNonIndividual,
  isHuf,
  isEntityOrNbfc,
  fileList = {},
  selfieFile,
}) => {
  if (backendStep === 1) {
    if (!selfieFile) return MANUAL_KYC_MSG.SELFIE_REQUIRED;
    if (!fileList.panDocument?.[0]) return MANUAL_KYC_MSG.PAN_DOC_REQUIRED;
    if (!fileList.idDocumentFront?.[0]) return MANUAL_KYC_MSG.AADHAAR_DOC_REQUIRED;
  }

  const isBankStep =
    (backendStep === 2 && !isNonIndividual) || backendStep === 3;
  if (
    isBankStep &&
    !fileList.cancelledCheque?.[0] &&
    !fileList.bankStatement?.[0] &&
    !fileList.bankProofImage?.[0]
  ) {
    return MANUAL_KYC_MSG.BANK_PROOF_REQUIRED;
  }

  if (backendStep === 2 && isNonIndividual) {
    if (!fileList.companyPanDocument?.[0] && !fileList.entityPanImage?.[0]) {
      return isHuf
        ? MANUAL_KYC_MSG.HUF_PAN_DOC_REQUIRED
        : MANUAL_KYC_MSG.ENTITY_PAN_DOC_REQUIRED;
    }
    if (
      isEntityOrNbfc &&
      !fileList.gstDocument?.[0] &&
      !fileList.gstImage?.[0]
    ) {
      return MANUAL_KYC_MSG.GST_DOC_REQUIRED;
    }
  }

  return null;
};

export const buildManualKycFormData = ({
  backendStep,
  userTypeId,
  values = {},
  fileList = {},
  selfieFile,
}) => {
  const formData = new FormData();
  formData.append("step", backendStep);
  formData.append("userTypeId", userTypeId);

  Object.keys(values).forEach((key) => {
    if (
      values[key] !== undefined &&
      values[key] !== null &&
      typeof values[key] !== "object"
    ) {
      formData.append(key, values[key]);
    }
  });

  if (values.pan) formData.set("pan", String(values.pan).toUpperCase());
  if (values.panNumber) formData.set("pan", String(values.panNumber).toUpperCase());
  if (values.entityPan) {
    formData.set("entityPan", String(values.entityPan).toUpperCase());
  }
  if (values.gstin) formData.set("gstin", String(values.gstin).toUpperCase());
  if (values.accountNumber) formData.append("accNum", values.accountNumber);
  if (values.ifscCode) {
    formData.append("ifsc", String(values.ifscCode).toUpperCase());
  }
  if (values.accountHolderName) {
    formData.append("accHolderName", values.accountHolderName);
  }
  if (backendStep === 1) {
    formData.set("idType", MANUAL_KYC_ID_TYPE);
    if (values.idNumber) {
      formData.set("idNumber", String(values.idNumber).trim());
    }
  }

  Object.keys(fileList).forEach((key) => {
    if (fileList[key]?.[0]) {
      formData.append(key, fileList[key][0].originFileObj || fileList[key][0]);
    }
  });

  if (selfieFile) {
    formData.append("selfieDocument", selfieFile);
  }

  return formData;
};

export const getNextManualStepIndex = ({
  backendStep,
  isNonIndividual,
  s2Status,
}) => {
  if (backendStep === 1) {
    const s2Done = isDoneStatus(s2Status);
    return s2Done && isNonIndividual ? 2 : 1;
  }
  if (backendStep === 2 && isNonIndividual) return 2;
  return null;
};

export const buildStepStatusArray = (length, activeStep) =>
  Array.from({ length }, (_, idx) => {
    if (idx < activeStep) return "finish";
    if (idx === activeStep) return "process";
    return "wait";
  });
